import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ZagButton } from '../button/button';
import { ZagIcon } from '../icon/icon';
import { BULK_JOBS_GATEWAY, type BulkJobsGateway } from './bulk-edit-jobs-gateway.token';
import type { BulkJobStatus } from './bulk-edit.models';
import type { BulkEditJobDescriptor, BulkJobToastData } from './bulk-edit-inline.types';

const STATUS_ICONS: Record<BulkJobStatus, string> = {
  running: 'sync',
  finished: 'check-circle',
  failed: 'error',
  cancelled: 'cancel',
};

@Component({
  selector: 'zag-bulk-edit-job-toast',
  templateUrl: './bulk-edit-job-toast.component.html',
  styleUrl: './bulk-edit-job-toast.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, ZagButton, ZagIcon],
})
export class BulkEditJobToastComponent {
  readonly job = input.required<BulkEditJobDescriptor>();
  readonly dismissed = output<string>();
  readonly detailsRequested = output<BulkJobToastData>();

  private readonly gateway = inject<BulkJobsGateway>(BULK_JOBS_GATEWAY);
  protected readonly visible = signal(true);
  private readonly gatewayJobId = computed(() => this.job().gatewayJobId ?? this.job().jobId);

  protected readonly progressEvent = computed(() => {
    const job = this.job();
    return this.gateway.getJobProgressEvent(job.queueName, this.gatewayJobId())();
  });

  protected readonly completedEvent = computed(() => {
    const job = this.job();
    return this.gateway.getJobCompleted(job.queueName, this.gatewayJobId())();
  });

  protected readonly errorEvent = computed(() => {
    const job = this.job();
    return this.gateway.getJobError?.(job.queueName, this.gatewayJobId())?.();
  });

  protected readonly progress = computed(() => (this.completedEvent() ? 100 : clampProgress(this.progressEvent()?.progress ?? 0)));

  protected readonly terminalStatus = computed<BulkJobStatus | null>(() => {
    const completed = this.completedEvent();
    if (completed) return readTerminalStatus(completed.result);
    return this.errorEvent() === undefined ? null : 'failed';
  });

  protected readonly terminalIcon = computed(() => {
    return STATUS_ICONS[this.terminalStatus() ?? 'running'];
  });

  constructor() {
    effect(onCleanup => {
      const job = this.job();
      const gatewayJobId = job.gatewayJobId ?? job.jobId;
      this.gateway.subscribe(job.queueName, gatewayJobId);
      onCleanup(() => this.gateway.unsubscribe(job.queueName, gatewayJobId));
    });
  }

  protected dismiss(): void {
    this.visible.set(false);
    this.dismissed.emit(this.job().jobId);
  }

  protected showDetails(): void {
    this.detailsRequested.emit(this.job());
  }
}

function clampProgress(progress: number): number {
  return Math.min(100, Math.max(0, progress));
}

function readTerminalStatus(value: unknown): BulkJobStatus {
  if (!isRecord(value)) return 'finished';
  const totals = value['totals'];
  if (isRecord(totals)) {
    if (typeof totals['failed'] === 'number' && totals['failed'] > 0) return 'failed';
    if (typeof totals['cancelled'] === 'number' && totals['cancelled'] > 0) return 'cancelled';
  }

  const status = value['status'];
  return status === 'failed' || status === 'cancelled' || status === 'finished' ? status : 'finished';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
